import {
  FC,
  Suspense,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { assetPairToTradeUrlKey, AssetLike } from "../../services/assetUrl";
import { AlgorithmicPairPickerQuery } from "./__generated__/AlgorithmicPairPickerQuery.graphql";
import { ALL_PAIRS, PairOption, PairSelect } from "./PairSelect";

// Exported so AlgorithmicLeaderboard's filter panel can reuse this same query.
export const query = graphql`
  query AlgorithmicPairPickerQuery {
    finV3 {
      pairs(first: 200, sortBy: NAME, sortDir: ASC) {
        edges {
          node {
            assetBase {
              asset
              type
              chain
              metadata {
                symbol
                decimals
              }
            }
            assetQuote {
              asset
              type
              chain
              metadata {
                symbol
                decimals
              }
            }
          }
        }
      }
    }
  }
`;

export type PairNode = NonNullable<
  NonNullable<
    NonNullable<
      AlgorithmicPairPickerQuery["response"]["finV3"]["pairs"]
    >["edges"]
  >[number]
>["node"];

export type PairAsset = NonNullable<PairNode>["assetBase"];

// This page always wants a pair selected (it drives the "Create strategy"
// link below it), so we pre-select this pair once the list loads instead of
// starting on "All pairs".
export const DEFAULT_PAIR_KEY = "ETH-BTC";

// Deliberately not DenomPair<AssetLike> — AssetLike.type is optional (shared
// by several URL-building helpers with looser data), which doesn't satisfy
// DenomPair's LabelAsset constraint (type: required).
export interface SelectedPairAssets {
  baseAsset: AssetLike;
  quoteAsset: AssetLike;
}

interface PairPickerProps {
  className?: string;
  selectedPairKey?: string;
  initialPairKey?: string;
  onSelect?: (asset: SelectedPairAssets | undefined) => void;
  onPairChange?: (pairKey: string) => void;
  onUnavailablePairKey?: (pairKey: string) => void;
}

// A standalone pair picker independent of the leaderboard below it — its own
// query against finV3.pairs (the full tradeable universe, not just pairs with
// open/closed range positions) and its own selection state.
const PairPickerContent: FC<PairPickerProps> = ({
  className,
  selectedPairKey,
  initialPairKey,
  onSelect,
  onPairChange,
  onUnavailablePairKey,
}) => {
  const data = useLazyLoadQuery<AlgorithmicPairPickerQuery>(query, {});
  const onSelectRef = useRef(onSelect);

  useLayoutEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const options = useMemo<PairOption<PairAsset>[]>(() => {
    const edges = data.finV3.pairs?.edges ?? [];
    return edges
      .map((e) => e?.node)
      .filter((n): n is NonNullable<PairNode> => !!n)
      .map((n) => ({
        key: assetPairToTradeUrlKey(n.assetBase, n.assetQuote),
        asset: { baseAsset: n.assetBase, quoteAsset: n.assetQuote },
      }));
  }, [data]);

  // useLazyLoadQuery + Suspense means `options` is already available on
  // first render, so the default can be computed directly in the initializer
  // — no flash of "All pairs" before it flips to the real default.
  const [pair, setPair] = useState<string>(
    () => initialPairKey ?? DEFAULT_PAIR_KEY
  );

  useLayoutEffect(() => {
    const requested = selectedPairKey ?? initialPairKey ?? DEFAULT_PAIR_KEY;
    const hasRequested = options.some((o) => o.key === requested);
    const next = hasRequested
      ? requested
      : (options.find((o) => o.key === DEFAULT_PAIR_KEY)?.key ?? ALL_PAIRS);
    if (!hasRequested) onUnavailablePairKey?.(requested);
    setPair(next);
  }, [initialPairKey, onUnavailablePairKey, options, selectedPairKey]);

  useLayoutEffect(() => {
    // `options` is intentional here: Relay can refresh objects while the
    // stable pair key stays the same, and the selected assets must be rebound.
    onSelectRef.current?.(options.find((o) => o.key === pair)?.asset);
  }, [pair, options]);

  return (
    <PairSelect
      options={options}
      pair={pair}
      setPair={(nextPair) => {
        setPair(nextPair);
        onPairChange?.(nextPair);
      }}
      clearable={false}
      className={className}
    />
  );
};

export const AlgorithmicPairPicker: FC<PairPickerProps> = ({
  className,
  selectedPairKey,
  initialPairKey,
  onSelect,
  onPairChange,
  onUnavailablePairKey,
}) => (
  <Suspense fallback={<div className="skeleton h-3 w-12 br-2" />}>
    <PairPickerContent
      className={className}
      selectedPairKey={selectedPairKey}
      initialPairKey={initialPairKey}
      onSelect={onSelect}
      onPairChange={onPairChange}
      onUnavailablePairKey={onUnavailablePairKey}
    />
  </Suspense>
);
